import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ShieldCheck, MapPin, Leaf, FileText, ChevronRight } from 'lucide-react';

export default function FarmerProfilePage() {
  const { farmerId } = useParams();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/farmers/${farmerId}`).then(r => { setFarmer(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [farmerId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!farmer) return <div className="min-h-screen flex items-center justify-center"><p>Farmer not found</p></div>;

  return (
    <div data-testid="farmer-profile-page">
      {/* Cover Image */}
      <div className="relative h-72 sm:h-96">
        <img src={farmer.cover_image || farmer.profile_image} alt="" className="w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10 pb-16">
        {/* Profile Header */}
        <div className="flex items-end gap-6 mb-10">
          <img data-testid="farmer-profile-image" src={farmer.profile_image} alt={farmer.name}
            className="w-28 h-28 sm:w-36 sm:h-36 rounded-3xl object-cover border-4 border-background shadow-lg" />
          <div className="pb-2">
            <div className="flex items-center gap-2 mb-1">
              <h1 data-testid="farmer-name" className="text-3xl sm:text-4xl font-serif font-medium text-white sm:text-foreground">{farmer.name}</h1>
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {farmer.farm_name} &middot; {farmer.location}
            </p>
          </div>
        </div>

        {/* Farming Practices */}
        <div className="flex flex-wrap gap-2 mb-8">
          {farmer.farming_practices?.map((p, i) => (
            <Badge key={i} variant="secondary" className="rounded-full px-4 py-1.5 text-xs">
              <Leaf className="h-3 w-3 mr-1.5" />{p}
            </Badge>
          ))}
        </div>

        {/* Story */}
        <section className="mb-12">
          <h2 className="text-2xl font-serif font-medium mb-4">Our Story</h2>
          <p data-testid="farmer-story" className="text-muted-foreground leading-relaxed">{farmer.story}</p>
        </section>

        {/* Gallery */}
        {farmer.gallery?.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-serif font-medium mb-4">Farm Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {farmer.gallery.map((img, i) => (
                <div key={i} className="rounded-3xl overflow-hidden aspect-[4/3]">
                  <img src={img} alt="" className="w-full h-full object-cover img-zoom" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Lab Reports */}
        {farmer.lab_reports?.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-serif font-medium mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" /> Lab Reports & Certifications
            </h2>
            <div className="space-y-2">
              {farmer.lab_reports.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noreferrer" data-testid={`lab-report-${i}`}
                  className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-sm font-medium">{r.name}</span>
                  <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                </a>
              ))}
            </div>
          </section>
        )}

        <Separator className="my-10" />

        {/* Products */}
        <section>
          <h2 className="text-2xl font-serif font-medium mb-6">Products from {farmer.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(farmer.products || []).map((p) => (
              <Link key={p.product_id} to={`/product/${p.product_id}`} data-testid={`farmer-product-${p.product_id}`}
                className="bg-card rounded-3xl overflow-hidden border border-border/50 product-card shadow-sm">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover img-zoom" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif font-medium text-base mb-1">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{p.variety}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">{'\u20B9'}{p.variants?.[0]?.price || p.price_per_kg}</span>
                    <span className="text-xs text-muted-foreground">{p.variants?.[0]?.weight || 'per kg'}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
